import { BoundingSphere, Cartesian2, Cartesian3, Color, ConstantPositionProperty, ConstantProperty, HeadingPitchRange, Math as CMath, PolylineOutlineMaterialProperty, Ray, } from "cesium";
import { cloneDeep } from "lodash";
import { AssetType } from "../../types";
import { CesiumUtils } from "../../utilities/cesium-utils";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { DEFAULT_DXF_LABEL_COLOR, DEFAULT_DXF_TEXT_SPHERE_COLOR, } from "./defaults";
import { TransformationUtil } from "../../utilities/transformation-utils";
import { FunctionLogger } from "../../utilities/function-logger";
export class EarthbrainDXF {
    _id;
    _heightMap;
    // private _entity: Entity[];
    _viewer;
    _site;
    _dxfData;
    _dxfOriginalData;
    _wgs84Data;
    _renderData;
    constructor(id, viewer, site) {
        this._id = id;
        this._viewer = viewer;
        this._site = site;
        this._dxfData = { id: -1, polylines: [], texts: [] };
        this._dxfOriginalData = { id: -1, polylines: [], texts: [] };
        this._wgs84Data = {
            polylines: [],
            texts: [],
            boundingSphere: undefined,
        };
        this._renderData = { lineEntities: [], infoEntities: [] };
    }
    getDXFData() {
        return this._dxfData;
    }
    async addDxfData(dxfOBJ) {
        this.dispose();
        this._dxfData = cloneDeep(dxfOBJ);
        this._dxfOriginalData = cloneDeep(dxfOBJ);
        await this.prepareWGS84Data();
        this._initializeEntities();
        this.renderDXFData();
    }
    zoomTo() {
        this._wgs84Data.boundingSphere &&
            this._viewer.camera.flyToBoundingSphere(this._wgs84Data.boundingSphere, {
                offset: new HeadingPitchRange(CMath.toRadians(0.0), CMath.toRadians(-30.0), 0.0),
            });
    }
    async prepareWGS84Data() {
        this._wgs84Data.polylines.length = 0;
        this._wgs84Data.texts.length = 0;
        const localPolylinePoints = this._dxfData.polylines.flatMap((polyline) => polyline.points);
        FunctionLogger.log(`Total number of Points to convert: ${localPolylinePoints.length}`);
        const wgs84PolylinePoints = await TransformationUtil.transformLocalPointsToWGS84InBatches(localPolylinePoints, this._site, 10000);
        for (let i = 0; i < this._dxfData.polylines.length; ++i) {
            const wgs84Points = wgs84PolylinePoints.splice(0, this._dxfData.polylines[i].points.length);
            this._wgs84Data.polylines.push({
                wgs84Points,
                color: this._dxfData.polylines[i].color,
            });
            if (wgs84Points.length > 0) {
                if (!this._wgs84Data.boundingSphere) {
                    this._wgs84Data.boundingSphere =
                        BoundingSphere.fromPoints(wgs84Points);
                }
                else {
                    BoundingSphere.fromBoundingSpheres([
                        this._wgs84Data.boundingSphere,
                        BoundingSphere.fromPoints(wgs84Points),
                    ], this._wgs84Data.boundingSphere);
                }
            }
        }
        if (this._dxfData.texts.length > 0) {
            const wgsSphereLocations = await this._site.convertPointsToWGS84(this._dxfData.texts.map((t) => new Cartesian3(t.labelPosition.x, t.labelPosition.y, t.labelPosition.z)));
            const wgsTextLocations = await this._site.convertPointsToWGS84(this._dxfData.texts.map((t) => new Cartesian3(t.position.x, t.position.y, t.position.z)));
            this._wgs84Data.texts = this._dxfData.texts.map((t, i) => ({
                label: t.label,
                wgs84LabelPosition: wgsTextLocations[i],
                wgs84Position: wgsSphereLocations[i],
            }));
        }
    }
    async _initializeEntities() {
        this._renderData.lineEntities.length = 0;
        this._renderData.infoEntities.length = 0;
        this._dxfData.polylines.forEach((d) => {
            const material = new PolylineOutlineMaterialProperty({
                color: d.color,
                outlineColor: d.color,
                outlineWidth: 2,
            });
            const entity = CesiumUtils.createPolyLine([], this._dxfData.id.toString(), 2, material, false, true, false);
            this._viewer.entities.add(entity);
            this._renderData.lineEntities.push(entity);
        });
        this._wgs84Data.texts.forEach((d) => {
            const entity = EarthBrainUtils.addLabel(this._viewer, d.wgs84LabelPosition, d.label, {
                fillColor: Color.fromCssColorString(DEFAULT_DXF_LABEL_COLOR),
                disableDepthDistance: Number.POSITIVE_INFINITY,
            });
            const sphere = EarthBrainUtils.addSphere(this._viewer, d.wgs84Position, Color.fromCssColorString(DEFAULT_DXF_TEXT_SPHERE_COLOR), Number.POSITIVE_INFINITY);
            this._renderData.infoEntities.push(entity, sphere);
        });
    }
    get id() {
        return this._id;
    }
    get heightMap() {
        return this._heightMap;
    }
    get assetType() {
        return AssetType.DXF_Data;
    }
    setVisibility(visible) {
        this._renderData.lineEntities.forEach((e) => (e.show = visible));
        this._renderData.infoEntities.forEach((e) => (e.show = visible));
        this._viewer.scene.requestRender();
    }
    updateColor(color) {
        const material = new PolylineOutlineMaterialProperty({
            color,
            outlineColor: color,
            outlineWidth: 2,
        });
        this._renderData.lineEntities.forEach((entity) => (entity.polyline.material = material));
        this._viewer.scene.requestRender();
    }
    dispose() {
        this._renderData.lineEntities.forEach((e) => this._viewer.entities.remove(e));
        this._renderData.infoEntities.forEach((e) => this._viewer.entities.remove(e));
        this._dxfData.polylines.length = 0;
        this._dxfData.texts.length = 0;
        this._dxfOriginalData.polylines.length = 0;
        this._dxfOriginalData.texts.length = 0;
        this._renderData.infoEntities.length = 0;
        this._renderData.lineEntities.length = 0;
        this._wgs84Data.polylines.length = 0;
        this._wgs84Data.texts.length = 0;
    }
    async adjustHeight(asset) {
        FunctionLogger.startLogging("adjustHeight");
        FunctionLogger.log("start", true);
        try {
            FunctionLogger.log("start Projection By Raycasting", true);
            for (const lineInfo of this._dxfData.polylines) {
                lineInfo.points = lineInfo.points
                    .map((p) => {
                    const ray = new Ray(new Cartesian3(p.x, p.y, 0), new Cartesian3(0, 0, 1));
                    const pointOnTileset = asset.heightMap.rayCast(ray);
                    return pointOnTileset;
                })
                    .filter((val) => val);
                // Below Check is added so that the DXF projected points which lies on the extra height map points(Sudden drop in elevation) are cleaned
                // Issue Link: https://github.com/EARTHBRAIN/sc_design_app/issues/1508
                // ignore the points whose elevation difference is more than elevationLimit and horizontalLimit
                const elevationLimit = 15;
                const horizontalLimit = 5;
                const cleanedPoints = [];
                for (let i = 0; i < lineInfo.points.length; i++) {
                    const prevPoint = cleanedPoints[cleanedPoints.length - 1];
                    const currentPoint = lineInfo.points[i];
                    if (prevPoint &&
                        Math.abs(currentPoint.z - prevPoint.z) > elevationLimit &&
                        Cartesian2.distance(Cartesian2.fromCartesian3(prevPoint), Cartesian2.fromCartesian3(currentPoint)) < horizontalLimit) {
                        continue;
                    }
                    cleanedPoints.push(currentPoint);
                }
                lineInfo.points = cleanedPoints;
            }
            for (const textInfo of this._dxfData.texts) {
                const textRay = new Ray(new Cartesian3(textInfo.position.x, textInfo.position.y, 0), new Cartesian3(0, 0, 1));
                const textPointOnTileset = asset.heightMap.rayCast(textRay);
                textInfo.position = textPointOnTileset;
                const labelRay = new Ray(new Cartesian3(textInfo.labelPosition.x, textInfo.labelPosition.y, 0), new Cartesian3(0, 0, 1));
                const labelPointOnTileset = asset.heightMap.rayCast(labelRay);
                textInfo.labelPosition = labelPointOnTileset;
            }
            this._dxfData.texts = this._dxfData.texts.filter((textInfo) => textInfo.position && textInfo.labelPosition);
            FunctionLogger.log("end Projection By Raycasting", true);
            FunctionLogger.log("start Transformation Of Points", true);
            await this.prepareWGS84Data();
            FunctionLogger.log("end Transformation Of Points", true);
            FunctionLogger.log("render requested");
            this.renderDXFData();
        }
        catch (error) {
            console.warn(error);
        }
        FunctionLogger.log("end", true);
        FunctionLogger.endLogging();
    }
    /**
     * Converts the local dxf coordinates into wgs84 coordinates and renders them.
     */
    renderDXFData() {
        if (this._wgs84Data.polylines.length === this._renderData.lineEntities.length) {
            for (let i = 0; i < this._renderData.lineEntities.length; ++i) {
                const points = this._wgs84Data.polylines[i].wgs84Points;
                this._renderData.lineEntities[i].polyline.positions =
                    new ConstantProperty(points);
            }
        }
        if (this._wgs84Data.texts.length * 2 ===
            this._renderData.infoEntities.length) {
            for (let i = 0; i < this._renderData.infoEntities.length; i += 2) {
                const textIndex = i / 2;
                const labelPosition = this._wgs84Data.texts[textIndex].wgs84LabelPosition;
                this._renderData.infoEntities[i].position =
                    new ConstantPositionProperty(labelPosition);
                const spherePosition = this._wgs84Data.texts[textIndex].wgs84Position;
                this._renderData.infoEntities[i + 1].position =
                    new ConstantPositionProperty(spherePosition);
            }
        }
        this._viewer.scene.requestRender();
    }
    /**
     * resets the z position of the dxf coordinates to zero
     */
    async resetHeight() {
        try {
            this._dxfData = cloneDeep(this._dxfOriginalData);
            await this.prepareWGS84Data();
            this.renderDXFData();
        }
        catch (error) {
            console.warn(error);
        }
    }
    static async fromJSON(id, json, viewer, site) {
        let dxfData;
        if (typeof json === "string") {
            dxfData = JSON.parse(json);
        }
        else if (typeof json === "object") {
            dxfData = json;
        }
        const dxfObj = { id, polylines: [], texts: [] };
        for (let i = 0; i < dxfData.polylines.length; i++) {
            const positions = dxfData.polylines[i].vertices.map((vertex) => new Cartesian3(vertex[0], vertex[1], 0));
            dxfObj.polylines.push({
                color: Color.fromCssColorString(`rgb(${dxfData.polylines[i].rgb})`),
                points: positions,
            });
        }
        if (dxfData.entities) {
            for (const entity of dxfData.entities) {
                dxfObj.texts.push({
                    label: entity.string,
                    labelPosition: new Cartesian3(entity.x2, entity.y2, entity.z2),
                    position: new Cartesian3(entity.x, entity.y, entity.z),
                });
            }
        }
        const dxf = new EarthbrainDXF(id, viewer, site);
        await dxf.addDxfData(dxfObj);
        return dxf;
    }
}
//# sourceMappingURL=earthbrain-dxf.js.map